import React, { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { DisplayToggle } from '@components/shared';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, RewardsSongInfoFragmentDoc } from '@graphql/reactQuery';
import { SONG_PAGE_REWARDS } from '@utils/constants';
import { SectionTitle } from '../shared';
import { Carousel } from './Carousel';
import { AudioRewardCard, RewardCard } from './RewardCard';
import { CardContainer, Container, RewardsContainer } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RewardsSongInfo on Release {
    id
    rewards {
      id
      ...RewardSongInfo
    }
    canCollectorsDownloadAudio
    auctionContractType
    goldenEggNft {
      id
      owner {
        id
        ...GoldenEggWinner
      }
    }
    ...AudioRewardCard
  }
`);

export const RewardComponent = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<RewardsSongInfoFragmentDoc>;
}) => {
  const release = getFragment(RewardsSongInfoFragmentDoc, releaseFrag);
  const { rewards, canCollectorsDownloadAudio, goldenEggNft, auctionContractType } = release;

  const isTieredRelease = auctionContractType === 'TIERED_EDITION';

  const DEFAULT_SLIDES = Object.values(SONG_PAGE_REWARDS)
    .map(rewardType => {
      const isGolden = rewardType === 'GOLDEN_EGG';

      if (rewardType === 'AUDIO_DOWNLOAD') {
        if (!canCollectorsDownloadAudio)
          return {
            id: rewardType,
            slide: null,
          };

        return {
          id: rewardType,
          slide: (
            <CardContainer key={rewardType}>
              <AudioRewardCard data={release} />
            </CardContainer>
          ),
        };
      }

      return {
        id: rewardType,
        slide: (
          <CardContainer key={rewardType}>
            <RewardCard
              rewardType={rewardType}
              goldenEggWinnerFrag={isGolden ? goldenEggNft?.owner : undefined}
              isTieredRelease={isTieredRelease}
            />
          </CardContainer>
        ),
      };
    })
    // This ensure that the carousel doesn't render empty slides
    .filter(slide => slide.slide);

  const rewardSlides = useMemo(() => {
    const rewardSlides = rewards?.map((reward, index) => {
      return {
        id: reward.id,
        slide: (
          <CardContainer key={reward.id}>
            <RewardCard rewardFrag={reward} index={index + 1} isTieredRelease={isTieredRelease} />
          </CardContainer>
        ),
      };
    });

    return rewardSlides ? [...DEFAULT_SLIDES, ...rewardSlides] : [...DEFAULT_SLIDES];
  }, [DEFAULT_SLIDES, isTieredRelease, rewards]);

  return (
    <Container>
      <SectionTitle title="Rewards" />
      <RewardsContainer>
        {Object.values(SONG_PAGE_REWARDS).map(rewardType => {
          if (rewardType === 'AUDIO_DOWNLOAD') {
            if (!canCollectorsDownloadAudio) return null;
            return <AudioRewardCard key={rewardType} data={release} />;
          }

          const isGolden = rewardType === 'GOLDEN_EGG';

          return (
            <RewardCard
              rewardType={rewardType}
              key={rewardType}
              goldenEggWinnerFrag={isGolden ? goldenEggNft?.owner : undefined}
              isTieredRelease={isTieredRelease}
            />
          );
        })}
        {rewards?.map((reward, i) => (
          <RewardCard
            key={reward.id}
            rewardFrag={reward}
            index={i + 1}
            isTieredRelease={isTieredRelease}
          />
        ))}
      </RewardsContainer>
      <DisplayToggle media="notTablet">
        <Carousel slides={rewardSlides} />
      </DisplayToggle>
    </Container>
  );
};
